import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { AuthLayout, TotpStep } from "./Login";
import { CodeGate } from "../components/CodeGate";
import { Button, Checkbox, ErrorBox, Field, Input, Spinner } from "../components/kd";

const P0 = { name: "", strasse: "", plz: "", ort: "", telefon: "", kzv_abrechnungsnummer: "" };

function F({ label, v, set, req, type = "text", id }: any) {
  return <Field label={label} required={req} htmlFor={id}><Input id={id} type={type} value={v} onChange={(e) => set(e.target.value)} data-testid={`reg-${id}-input`} /></Field>;
}

export default function Register() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const einladung = sp.get("einladung");
  const [stage, setStage] = useState<"code" | "daten" | "totp">("code");
  const [email, setEmail] = useState("");
  const [invite, setInvite] = useState<any>(null);
  const [p, setP] = useState<any>(P0);
  const [me, setMe] = useState({ vorname: "", nachname: "", pw: "", pw2: "" });
  const [ok, setOk] = useState(false);
  const [setup, setSetup] = useState<any>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (einladung) api.get(`/public/einladung/${einladung}`).then((r) => setInvite(r.data)).catch((e) => setErr(errMsg(e))); }, [einladung]);
  if (einladung && !invite && !err) return <AuthLayout><Spinner /></AuthLayout>;
  const team = invite?.art === "TEAM_MITGLIED";

  const submit = async () => {
    setErr("");
    if (me.pw.length < 12) return setErr("Das Passwort muss mindestens 12 Zeichen lang sein.");
    if (me.pw !== me.pw2) return setErr("Die Passwörter stimmen nicht überein.");
    if (!ok) return setErr("Bitte stimmen Sie den Nutzungsbedingungen und der Datenschutzerklärung zu.");
    setBusy(true);
    try {
      await api.post("/konto/registrieren", { praxis: team ? null : { ...p, kzv_abrechnungsnummer: p.kzv_abrechnungsnummer || null }, vorname: me.vorname, nachname: me.nachname,
        passwort: me.pw, zustimmung: ok, einladung, praxis_slug: sp.get("praxis") });
      setSetup((await api.get("/auth/totp/einrichtung")).data);
      setStage("totp");
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <AuthLayout>
      <h1 className="mb-1">Konto für Überweiserpraxen</h1>
      {invite && <p className="mb-3 text-text-muted" data-testid="reg-invite-info">{invite.fachpraxis ? `Einladung von ${invite.fachpraxis.name}` : "Einladung in ein Praxisteam"}</p>}
      <ErrorBox testid="reg-error">{err}</ErrorBox>
      {stage === "code" && (
        <CodeGate title="E-Mail bestätigen" fixedEmailHint={invite?.email}
          onRequest={(e) => api.post("/konto/registrieren/code", { email: invite?.email || e })}
          onVerify={async (e0, code) => { const e = invite?.email || e0; await api.post("/konto/registrieren/code/pruefen", { email: e, code }); setEmail(e); setStage("daten"); }} />
      )}
      {stage === "daten" && (
        <div className="kd-fade" data-testid="reg-form">
          <p className="mb-4 text-sm text-success">E-Mail bestätigt: {email}</p>
          {!team && (<>
            <h2 className="mb-3">Praxisdaten</h2>
            <F id="pname" label="Praxisname" req v={p.name} set={(v: string) => setP({ ...p, name: v })} />
            <F id="ptel" label="Telefon" req v={p.telefon} set={(v: string) => setP({ ...p, telefon: v })} />
            <F id="pstr" label="Straße" v={p.strasse} set={(v: string) => setP({ ...p, strasse: v })} />
            <div className="grid grid-cols-3 gap-3"><F id="pplz" label="PLZ" v={p.plz} set={(v: string) => setP({ ...p, plz: v })} /><div className="col-span-2"><F id="port" label="Ort" v={p.ort} set={(v: string) => setP({ ...p, ort: v })} /></div></div>
            <F id="pkzv" label="KZV-Abrechnungsnummer (optional)" v={p.kzv_abrechnungsnummer} set={(v: string) => setP({ ...p, kzv_abrechnungsnummer: v })} />
          </>)}
          <h2 className="mb-3 mt-2">Ihre Daten</h2>
          <F id="vorname" label="Vorname" req v={me.vorname} set={(v: string) => setMe({ ...me, vorname: v })} />
          <F id="nachname" label="Nachname" req v={me.nachname} set={(v: string) => setMe({ ...me, nachname: v })} />
          <F id="pw" label="Passwort (mind. 12 Zeichen)" req type="password" v={me.pw} set={(v: string) => setMe({ ...me, pw: v })} />
          <F id="pw2" label="Passwort wiederholen" req type="password" v={me.pw2} set={(v: string) => setMe({ ...me, pw2: v })} />
          <Checkbox testid="reg-consent" checked={ok} onChange={setOk} label={<>Ich stimme den <Link to="/nutzungsbedingungen" target="_blank">Nutzungsbedingungen</Link> und der <Link to="/datenschutz" target="_blank">Datenschutzerklärung</Link> (Version 1.0) zu.</>} />
          <Button className="mt-4 w-full" onClick={submit} loading={busy} data-testid="reg-submit-button">Weiter zur Zwei-Faktor-Einrichtung</Button>
        </div>
      )}
      {stage === "totp" && <TotpStep setup={setup} onDone={() => nav("/ueberweiser", { replace: true })} />}
    </AuthLayout>
  );
}

export function InvitationPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [inv, setInv] = useState<any>(null);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  useEffect(() => { api.get(`/public/einladung/${token}`).then((r) => setInv(r.data)).catch((e) => setErr(errMsg(e))); }, [token]);
  const accept = async () => {
    try { await api.post(`/public/einladung/${token}/annehmen`); setDone(true); } catch (e: any) {
      if (e?.response?.status === 401) nav(`/login?weiter=/einladung/${token}`); else setErr(errMsg(e));
    }
  };
  return (
    <AuthLayout>
      <h1 className="mb-3">Einladung</h1>
      <ErrorBox testid="invite-error">{err}</ErrorBox>
      {!inv && !err && <Spinner />}
      {inv && !done && (
        <div data-testid="invite-info">
          <p className="mb-4">{inv.art === "TEAM_MITGLIED" ? "Sie wurden in das Team einer Überweiserpraxis eingeladen." : `${inv.fachpraxis?.name} lädt Sie ein, Patienten digital zu überweisen.`}</p>
          {inv.registriert ? (
            <Button onClick={accept} data-testid="invite-accept-button">Verbindung bestätigen</Button>
          ) : (
            <Button onClick={() => nav(`/registrieren?einladung=${token}`)} data-testid="invite-register-button">Einladung annehmen und Konto anlegen</Button>
          )}
        </div>
      )}
      {done && <p data-testid="invite-done">Die Verbindung ist aktiv. <Link to="/ueberweiser">Zum Überweiserbereich</Link></p>}
    </AuthLayout>
  );
}

export function UnsubscribePage() {
  const [sp] = useSearchParams();
  const [state, setState] = useState("");
  const go = async () => { try { await api.post("/public/abmelden", { e: sp.get("e"), s: sp.get("s") }); setState("ok"); } catch (e) { setState(errMsg(e)); } };
  return (
    <AuthLayout>
      <h1 className="mb-3">Von Einladungen abmelden</h1>
      {state === "ok" ? <p data-testid="unsub-done">Sie erhalten keine weiteren Einladungen an {sp.get("e")}.</p> : (
        <><ErrorBox>{state}</ErrorBox><p className="mb-4">Keine weiteren Einladungen an {sp.get("e")} senden?</p><Button onClick={go} data-testid="unsub-button">Abmelden</Button></>
      )}
    </AuthLayout>
  );
}
